import { useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldError, useController, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  AutocompleteRenderInputParams,
  Box,
  DatePicker,
  FormInputText,
  InputLabel,
  PhoneNumberInput,
  ProfileImage,
  Select,
  Svg,
  SvgBubble,
  Text,
  useMediaQuery,
} from '@hopskipdrive/react-components';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';
import { useFlags } from '@rideiq/contexts/LDContext';
import { Button } from '@rideiq/shared/components/Button';
import { colorTokens, variables } from '@rideiq/shared/components/variables';

import { analyticsClickUploadPhoto } from '../../../../../api/services/Analytics';
import { useCategories } from '../../../../../hooks/useCategories';
import { useGenders } from '../../../../../hooks/useGenders';
import { InvoiceGroupFormField } from '../../../InvoiceGroupDropdown';
import { SisImportBanner } from '../../../RiderCreationModal/SisImportBanner';
import { SisStudentDetails } from '../../../RiderCreationModal/SisImportBanner/types';
import { useAccounts } from '../../../SharedBilling/useAccounts';
import { useOrganizers } from '../../../SharedBilling/useOrganizers';
import { OptionCard } from '../../components/OptionCard';
import { useSisGate } from '../../hooks/useSisGate';
import { useSisImportStatus } from '../../hooks/useSisImportStatus';
import { RiderFormValues } from '../../types';

import {
  FieldCell,
  ImageWithIcon,
  PhotoUploadInstructions,
  SectionDivider,
  ToggleStack,
  TwoColGrid,
} from './BasicInfoSection.styles';
import { mapSisCaregiversToContacts, mapSisStudentToBasicInfoFields } from './sisImportMapping';

const AIDE_TOOLTIP =
  'Select this option if this profile is for a rider aide. A rider aide is an authorized adult who rides along with others for support and safety. Rider aides are not provided by HopSkipDrive. If that type of service is needed, do not select this option, request a Rider Assistant in the Safety Settings.';

type SBErrors = {
  residenceAccountId?: FieldError;
  residenceAccountContactId?: FieldError;
  residenceAccountContactName?: FieldError;
  residenceAccountContactEmail?: FieldError;
};

export const BasicInfoSection = () => {
  const {
    control,
    watch,
    clearErrors,
    setValue,
    formState: { errors },
  } = useFormContext<RiderFormValues>();

  const { slug } = useParams<{ slug?: string }>();
  const { isAdminOrOrganizerUser, userFeatures } = useAuthQuery();
  const { enableApprovalFlowForSharedRider } = useFlags();
  const { categories } = useCategories();
  const { genders } = useGenders();
  const sisEnabled = useSisGate();
  const sisStatus = useSisImportStatus(sisEnabled);

  const handleSisImport = (sisData: SisStudentDetails) => {
    const { firstName, lastName, email, externalId } = mapSisStudentToBasicInfoFields(sisData);

    setValue('firstName', firstName, { shouldDirty: true });
    setValue('lastName', lastName, { shouldDirty: true });
    setValue('email', email, { shouldDirty: true });
    setValue('externalId', externalId, { shouldDirty: true });
    setValue('sisImportUsed', true, { shouldDirty: false });

    if (sisData.caregivers.length > 0) {
      setValue('contacts', mapSisCaregiversToContacts(sisData.caregivers), { shouldDirty: true });
    }
  };

  const watchIsSharedBillingRider = watch('isSharedBillingRider');
  const watchResidenceAccountId = watch('sharedBillingRider.residenceAccountId');
  const watchInvoiceGroup = watch('invoiceGroup');

  const accounts = useAccounts();
  const { organizers, fetchOrganizers, clearOrganizers, isLoading: organizersLoading } = useOrganizers();

  const showInvoiceGroup = isAdminOrOrganizerUser;
  const showSharedBilling = !!userFeatures?.sharedBilling;
  const noAccounts = !accounts || accounts.length === 0;
  const sbErrors = errors.sharedBillingRider as SBErrors | undefined;

  useEffect(() => {
    if (enableApprovalFlowForSharedRider && watchResidenceAccountId) {
      fetchOrganizers(Number(watchResidenceAccountId));
    }
  }, [watchResidenceAccountId]);

  const photoError =
    errors.photosAttributes && (errors.photosAttributes[0]?.attachment as unknown as FieldError)?.message;
  const watchInitialPhotoUrl = watch('photosAttributes.0.url');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
      {sisEnabled && sisStatus && <SisImportBanner sisStatus={sisStatus} onImport={handleSisImport} />}

      <TwoColGrid>
        <FieldCell>
          <InputLabel fieldtype="required" htmlFor="firstName">
            First name
          </InputLabel>
          <Controller
            control={control}
            name="firstName"
            render={({ field: { value, onChange, onBlur } }) => (
              <FormInputText
                variant="outlined"
                id="firstName"
                name="firstName"
                inputProps={{ 'aria-required': true }}
                value={value}
                data-testid="firstName"
                fullWidth
                placeholder="Enter first name"
                autoComplete="given-name"
                error={!!errors.firstName}
                helperText={errors.firstName?.message as string | undefined}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
        </FieldCell>

        <FieldCell>
          <InputLabel fieldtype="required" htmlFor="lastName">
            Last name
          </InputLabel>
          <Controller
            control={control}
            name="lastName"
            render={({ field: { value, onChange, onBlur } }) => (
              <FormInputText
                variant="outlined"
                id="lastName"
                name="lastName"
                inputProps={{ 'aria-required': true }}
                value={value}
                data-testid="lastName"
                fullWidth
                placeholder="Enter last name"
                autoComplete="family-name"
                error={!!errors.lastName}
                helperText={errors.lastName?.message as string | undefined}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
        </FieldCell>

        <FieldCell>
          <InputLabel fieldtype="required" htmlFor="gender">
            Gender
          </InputLabel>
          <Controller
            control={control}
            name="gender"
            render={({ field: { value, onChange, onBlur } }) => (
              <Select
                id="gender"
                options={genders}
                value={(genders.length > 0 && value) || undefined}
                setValue={onChange}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    id="gender"
                    inputProps={{ ...params.inputProps, 'aria-required': true }}
                    fullWidth
                    placeholder="Select gender"
                    data-testid="gender"
                    autoComplete="off"
                    onBlur={onBlur}
                    error={!!errors.gender}
                    helperText={errors.gender?.message as string | undefined}
                  />
                )}
              />
            )}
          />
        </FieldCell>

        <FieldCell>
          <InputLabel fieldtype="required" htmlFor="dateOfBirth">
            Date of birth
          </InputLabel>
          <Controller
            control={control}
            name="dateOfBirth"
            render={({ field: { onChange, onBlur, value } }) => (
              <DatePicker
                onChange={onChange}
                onAccept={onBlur}
                value={value}
                slotProps={{
                  textField: {
                    id: 'dateOfBirth',
                    fullWidth: true,
                    onBlur,
                    error: !!errors.dateOfBirth,
                    helperText: errors.dateOfBirth?.message as string | undefined,
                    inputProps: { 'data-testid': 'date-of-birth-picker', autoComplete: 'bday', 'aria-required': true },
                  },
                }}
              />
            )}
          />
        </FieldCell>

        <FieldCell>
          <InputLabel htmlFor="email">Rider email</InputLabel>
          <Controller
            control={control}
            name="email"
            render={({ field: { value, onChange, onBlur } }) => (
              <FormInputText
                variant="outlined"
                id="email"
                name="email"
                data-testid="email"
                fullWidth
                placeholder="Enter email"
                autoComplete="email"
                value={value ?? ''}
                error={!!errors.email}
                helperText={errors.email?.message as string | undefined}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
        </FieldCell>

        <FieldCell>
          <InputLabel htmlFor="mobilePhoneNumber">Rider phone number</InputLabel>
          <Controller
            control={control}
            name="mobilePhoneNumber"
            render={({ field: { onChange, value, name } }) => (
              <PhoneNumberInput
                variant="outlined"
                id={name}
                name={name}
                fullWidth
                placeholder="Enter phone number"
                autoComplete="tel"
                data-testid={name}
                onValueChange={onChange}
                initialValue={value}
                error={!!errors.mobilePhoneNumber}
                helperText={errors.mobilePhoneNumber?.message as string | undefined}
              />
            )}
          />
        </FieldCell>
      </TwoColGrid>

      {/* Photo upload */}
      <PhotoUpload control={control} errorMessage={photoError} initialPhotoUrl={watchInitialPhotoUrl} />

      <SectionDivider />

      {/* Block 2 — Account & billing */}
      <TwoColGrid>
        <FieldCell>
          <InputLabel htmlFor="externalId">Rider ID</InputLabel>
          <Controller
            control={control}
            name="externalId"
            render={({ field: { onChange, value } }) => (
              <FormInputText
                variant="outlined"
                id="externalId"
                name="externalId"
                data-testid="idNumber"
                fullWidth
                placeholder="Enter rider ID"
                autoComplete="off"
                value={value}
                onChange={onChange}
              />
            )}
          />
        </FieldCell>

        {showInvoiceGroup && (
          <FieldCell>
            <InvoiceGroupFormField
              control={control as unknown as Control<RiderFormType>}
              initialValue={watchInvoiceGroup ?? undefined}
              selectDefault={!slug}
              onAutoSelect={(option) => setValue('invoiceGroupId', Number(option.value), { shouldDirty: false })}
              error={!!errors.invoiceGroupId}
              helperText={errors.invoiceGroupId?.message?.toString() ?? ''}
            />
          </FieldCell>
        )}

        <FieldCell>
          <InputLabel fieldtype="required" htmlFor="category">
            Rider category
          </InputLabel>
          <Controller
            control={control}
            name="category"
            defaultValue={categories[0]?.value}
            render={({ field: { value, onChange, onBlur } }) => (
              <Select
                id="category"
                options={categories}
                value={(categories.length > 0 && value) || undefined}
                setValue={onChange}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    id="category"
                    inputProps={{ ...params.inputProps, 'aria-required': true }}
                    data-testid="category-form"
                    fullWidth
                    placeholder="Select category"
                    autoComplete="off"
                    onBlur={onBlur}
                    error={!!errors.category}
                    helperText={errors.category?.message as string | undefined}
                  />
                )}
              />
            )}
          />
        </FieldCell>
      </TwoColGrid>

      <SectionDivider />

      {/* Block 3 — Toggle cards */}
      <ToggleStack>
        <Controller
          control={control}
          name="isAide"
          render={({ field: { value, onChange } }) => (
            <OptionCard label="This rider is an aide" checked={!!value} onChange={onChange} tooltip={AIDE_TOOLTIP} />
          )}
        />

        {showSharedBilling && (
          <>
            <Controller
              control={control}
              name="isSharedBillingRider"
              render={({ field: { value, onChange } }) => (
                <OptionCard
                  label="This rider's transportation costs are shared with another district"
                  checked={!!value}
                  onChange={(checked) => {
                    onChange(checked);
                    if (!checked) {
                      clearErrors(['sharedBillingRider']);
                    }
                  }}
                  disabled={noAccounts}
                />
              )}
            />

            {watchIsSharedBillingRider && (
              <TwoColGrid>
                <FieldCell>
                  <InputLabel fieldtype="required" htmlFor="residenceAccountId">
                    Residence District
                  </InputLabel>
                  <Controller
                    control={control}
                    name="sharedBillingRider.residenceAccountId"
                    render={({ field: { onChange, value } }) => (
                      <Select
                        id="residenceAccountId"
                        options={accounts?.map((a) => ({ label: a.name, value: a.id })) || []}
                        value={(accounts?.find((a) => a.id === value) && value) || undefined}
                        setValue={(v) => {
                          onChange(v);
                          if (enableApprovalFlowForSharedRider) {
                            if (!v) {
                              clearOrganizers();
                            }
                            setValue('sharedBillingRider.residenceAccountContactId', null);
                            setValue('sharedBillingRider.residenceAccountContactEmail', '');
                          }
                        }}
                        isLoading={!!value && !accounts}
                        renderInput={(params) => (
                          <FormInputText
                            {...params}
                            type="text"
                            disabled={(!!value && (!accounts || accounts.length === 0)) || !watchIsSharedBillingRider}
                            id="residenceAccountId"
                            inputProps={{ ...params.inputProps, 'aria-required': true }}
                            variant="outlined"
                            data-testid="residenceAccountId"
                            placeholder="School District"
                            error={!!sbErrors?.residenceAccountId}
                            helperText={sbErrors?.residenceAccountId?.message}
                          />
                        )}
                      />
                    )}
                  />
                </FieldCell>

                <FieldCell>
                  <InputLabel
                    fieldtype="required"
                    htmlFor={
                      enableApprovalFlowForSharedRider ? 'residenceAccountContactId' : 'residenceAccountContactName'
                    }
                  >
                    Contact Name for Notifications
                  </InputLabel>
                  {enableApprovalFlowForSharedRider ? (
                    <Controller
                      name="sharedBillingRider.residenceAccountContactId"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          id="residenceAccountContactId"
                          options={organizers?.map((o) => ({ label: o.name, value: o.id })) || []}
                          value={(organizers?.find((o) => o.id === value) && value) || undefined}
                          setValue={(v) => {
                            const organizerEmail = organizers?.find((o) => o.id === Number(v))?.email;

                            onChange(v);
                            setValue('sharedBillingRider.residenceAccountContactEmail', organizerEmail ?? '');
                          }}
                          isLoading={organizersLoading}
                          renderInput={(params) => (
                            <FormInputText
                              {...params}
                              type="text"
                              disabled={
                                (!!value && (!organizers || organizers.length === 0)) || !watchIsSharedBillingRider
                              }
                              id="residenceAccountContactId"
                              inputProps={{ ...params.inputProps, 'aria-required': true }}
                              variant="outlined"
                              data-testid="residenceAccountContactId"
                              placeholder="Enter contact name"
                              error={!!sbErrors?.residenceAccountContactId}
                              helperText={sbErrors?.residenceAccountContactId?.message}
                            />
                          )}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      name="sharedBillingRider.residenceAccountContactName"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <FormInputText
                          inputProps={{ 'aria-required': true }}
                          id="residenceAccountContactName"
                          name="sharedBillingRider.residenceAccountContactName"
                          value={value}
                          variant="outlined"
                          data-testid="residenceAccountContactName"
                          fullWidth
                          disabled={!watchIsSharedBillingRider}
                          placeholder="Enter contact name"
                          error={!!sbErrors?.residenceAccountContactName}
                          helperText={sbErrors?.residenceAccountContactName?.message}
                          onChange={onChange}
                        />
                      )}
                    />
                  )}
                </FieldCell>

                <FieldCell>
                  <InputLabel
                    fieldtype={!enableApprovalFlowForSharedRider ? 'required' : undefined}
                    htmlFor="residenceAccountContactEmail"
                  >
                    Contact Email
                  </InputLabel>
                  <Controller
                    name="sharedBillingRider.residenceAccountContactEmail"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormInputText
                        inputProps={{ 'aria-required': !enableApprovalFlowForSharedRider }}
                        id="residenceAccountContactEmail"
                        value={value}
                        name="sharedBillingRider.residenceAccountContactEmail"
                        variant="outlined"
                        data-testid="residenceAccountContactEmail"
                        fullWidth
                        placeholder="Enter contact email"
                        type="email"
                        disabled={enableApprovalFlowForSharedRider || !watchIsSharedBillingRider}
                        error={!!sbErrors?.residenceAccountContactEmail}
                        helperText={sbErrors?.residenceAccountContactEmail?.message}
                        onChange={onChange}
                      />
                    )}
                  />
                </FieldCell>
              </TwoColGrid>
            )}
          </>
        )}
      </ToggleStack>
    </Box>
  );
};

const PhotoUpload = ({
  control,
  errorMessage,
  initialPhotoUrl,
}: {
  control: Control<RiderFormValues>;
  errorMessage: string | undefined;
  initialPhotoUrl?: string;
}) => {
  const { field } = useController({ control, name: 'photosAttributes.0.attachment' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [preview, setPreview] = useState(initialPhotoUrl ?? '');
  const [fileSizeError, setFileSizeError] = useState('');
  const PHOTO_SIZE_LIMIT = 10e6;
  const isMobile = useMediaQuery({ maxWidth: variables.breakpoints.desktop - 1 });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      {preview ? (
        <ImageWithIcon>
          <ProfileImage size={96} imgSrc={preview} />
          <SvgBubble
            height="25px"
            width="25px"
            bgColor={colorTokens.background.default}
            onClick={() => {
              setValue('');
              setPreview('');
              setFileSizeError('');
              field.onChange('');
            }}
            data-testid="trashcan"
          >
            <Svg icon="trashcan" width="14px" />
          </SvgBubble>
        </ImageWithIcon>
      ) : (
        <Box
          sx={{
            width: '96px',
            height: '96px',
            borderRadius: '64px',
            backgroundColor: '#C5C6C9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            padding: '8px',
            flexShrink: 0,
            '& svg path': { fill: 'white' },
          }}
        >
          <Svg icon="person-filled" width="70px" height="70px" />
        </Box>
      )}
      <PhotoUploadInstructions>
        <Button
          color="secondary"
          size="medium"
          onClick={() => {
            analyticsClickUploadPhoto('Rider Form');
            fileInputRef?.current?.click();
          }}
        >
          Upload Photo
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            ref={fileInputRef}
            hidden
            value={value}
            data-testid="image-upload"
            onChange={(e) => {
              setValue(e.target.value);
              setFileSizeError('');
              if (e.target?.files && e.target?.files[0]) {
                const photoUpload = e.target.files[0];

                if (photoUpload.size > PHOTO_SIZE_LIMIT) {
                  setFileSizeError('Photo size is greater than 10MB.');
                } else {
                  setPreview(URL.createObjectURL(photoUpload));
                  field.onChange(e.target.files[0]);
                  field.onBlur();
                }
              }
            }}
          />
        </Button>
        <Text sx={{ fontSize: 12, color: colorTokens.text.medium, textAlign: 'center' }}>JPG or PNG, max 10MB</Text>
        {(fileSizeError || errorMessage) && <Text textColor="error">{fileSizeError || errorMessage}</Text>}
      </PhotoUploadInstructions>
    </Box>
  );
};
